import { ResponsiveImage as Image } from "@/components/ui/ResponsiveImage";
import { Link } from "@/components/ui/Link";
import { useCallback, useEffect, useRef, useState, type ComponentType, type CSSProperties } from "react";
import {
  ArrowRight, Newspaper, Building2, ChevronDown, ChartLine, MessageCircle,
  History, FolderOpen, LifeBuoy, List, Search,
  FilePenLine, PlugZap, ShoppingBag, Sparkles, ScanLine,
  Image as ImageIcon, Users, X, Video,
} from "lucide-react";
import { experienceContent } from "@/components/landing/experience-content";
import { buildAppLoginUrl, buildAppAuthUrl } from "@/auth/authLinks";
import { readAccountStateHint } from "@/auth/accountState";
import { useLandingIdentity } from "@/auth/authSession";
import { resourceLinks, type ResourceIcon } from "@/data/resource-content";
import { getToolStatusLabel } from "@/data/tool-content";
import { isVideoAnalysisEnabled, isVideoSearchEnabled } from "@/config/features";
import { toolsByLocale } from "./header-tools";
import type { Locale } from "@/types";
import { headerStyles } from "@/styles/classes";

const styles = { ...headerStyles, secondaryCta: "studio-header-secondaryCta", login: "studio-header-login" };
import { SkyToneIcon } from "@/components/ui/SkyToneIcon";
import { SKY_TONES, useSkyTheme } from "@/theme/sky-theme-core";
import { SKY_PALETTES, type SkyTone } from "@/data/sky";

export type HeaderMenuId = "agents" | "tools" | "integrations" | "resources";
type Icon = ComponentType<{ "aria-hidden"?: boolean | "true"; className?: string; weight?: "bold" | "fill" }>;

export interface LandingHeaderProps {
  variant: "air" | "site";
  tone?: "light" | "dark";
  skyTone?: SkyTone;
  locale?: Locale;
  introComplete?: boolean;
  scrolled?: boolean;
  showThemeControl?: boolean;
  onLocaleChange?: () => void;
}

interface MenuItem {
  title: string;
  description: string;
  href: string;
  icon: Icon;
  meta?: string;
  disabled?: boolean;
}

const resourceIcons: Record<ResourceIcon, Icon> = {
  article: Newspaper, plugs: PlugZap, lifebuoy: LifeBuoy,
  clock: History, building: Building2, chat: MessageCircle,
};
const agentIcons: readonly Icon[] = [Sparkles, Search, FilePenLine, Users, ImageIcon];
const toolIcons: readonly Icon[] = [ImageIcon, Search, ScanLine, ChartLine, Video, FilePenLine, Users];

function getHeaderSkyStyle(tone: SkyTone): CSSProperties {
  const palette = SKY_PALETTES[tone];
  return {
    "--theme-sky-top": palette.top,
    "--theme-sky-horizon": palette.horizon,
    "--theme-sky-bottom": palette.bottom,
    "--theme-sky-copy": tone === "sunrise" || tone === "sunset" ? "#182944" : "#f8faf6",
  } as CSSProperties;
}

const integrationContent = {
  en: [
    { id: "commerce-context", title: "Commerce context", description: "Product facts, catalogs, variants, and approved claims.", icon: ShoppingBag },
    { id: "creative-evidence", title: "Creative evidence", description: "Social, advertising, and performance context.", icon: ChartLine },
    { id: "asset-libraries", title: "Asset libraries", description: "Approved footage, campaigns, guidelines, and references.", icon: FolderOpen },
    { id: "team-handoffs", title: "Team handoffs", description: "Review states, briefs, scripts, and decisions.", icon: MessageCircle },
  ],
  zh: [
    { id: "commerce-context", title: "商业信息", description: "产品事实、商品目录、规格与已批准表述。", icon: ShoppingBag },
    { id: "creative-evidence", title: "创意证据", description: "社交、广告与效果数据背景。", icon: ChartLine },
    { id: "asset-libraries", title: "素材库", description: "已批准的素材、Campaign、规范与参考。", icon: FolderOpen },
    { id: "team-handoffs", title: "团队交接", description: "审核状态、Brief、脚本与决策。", icon: MessageCircle },
  ],
} as const;

const resourceZh: Record<string, Pick<MenuItem, "title" | "description">> = {
  "/help": { title: "帮助中心", description: "设置品牌 Context 与启动第一个项目。" },
  "/integrations": { title: "集成", description: "连接证据、素材与工作流。" },
  "/releases": { title: "更新记录", description: "清晰记录产品更新与改进。" },
  "/about": { title: "关于我们", description: "了解我们为何构建更好的创意决策系统。" },
  "/contact": { title: "联系我们", description: "沟通体验申请、合作或支持问题。" },
};

function getLabels(locale: Locale) {
  return locale === "zh"
    ? { agents: "Agents", tools: "工具", integrations: "集成", useCases: "使用场景", resources: "资源", blog: "博客", login: "登录", dashboard: "工作台", cta: "申请早期体验", signUp: "注册", language: "切换到英文", menu: "导航菜单", close: "关闭导航", viewAgents: "查看全部 Agents", viewTools: "查看全部工具", viewIntegrations: "查看集成规划", viewResources: "查看全部资源" }
    : { agents: "Agents", tools: "Tools", integrations: "Integrations", useCases: "Use Cases", resources: "Resources", blog: "Blog", login: "Log in", dashboard: "Dashboard", cta: "Request Early Access", signUp: "Sign up", language: "Switch to Chinese", menu: "Navigation menu", close: "Close navigation", viewAgents: "View all agents", viewTools: "View all tools", viewIntegrations: "View integrations", viewResources: "View all resources" };
}

function getMenuItems(menu: HeaderMenuId, locale: Locale): MenuItem[] {
  if (menu === "agents") {
    return experienceContent[locale].agents.map((agent, index) => ({
      title: agent.label,
      description: agent.role,
      href: `/agents/${agent.id}`,
      icon: agentIcons[index],
      meta: agent.kind === "orchestrator" ? (locale === "zh" ? "统筹" : "Orchestrator") : undefined,
    }));
  }
  if (menu === "tools") {
    return toolsByLocale[locale].filter((tool) => {
      if (tool.id === "video-search") return isVideoSearchEnabled(import.meta.env.MODE);
      if (tool.id === "video-analysis") return isVideoAnalysisEnabled(import.meta.env.MODE);
      return true;
    }).map((tool, index) => ({
      title: tool.label,
      description: tool.shortDescription,
      href: `/tools/${tool.id}`,
      icon: toolIcons[index],
      meta: getToolStatusLabel(tool.status, locale),
      disabled: tool.status === "coming-soon",
    }));
  }
  if (menu === "integrations") {
    return integrationContent[locale].map((item) => ({ ...item, href: `/integrations#${item.id}`, meta: locale === "zh" ? "规划中" : "Planned", disabled: true }));
  }
  return resourceLinks.filter((item) => item.href !== "/blog").map((item) => ({
    ...(locale === "zh" ? resourceZh[item.href] : item),
    href: item.href,
    icon: resourceIcons[item.icon],
    disabled: item.planned,
    meta: item.planned ? (locale === "zh" ? "规划中" : "Planned") : undefined,
  }));
}

export function LandingHeader({ variant, tone = "dark", skyTone: sceneTone, locale = "en", introComplete = true, scrolled = false, showThemeControl = variant === "site", onLocaleChange }: LandingHeaderProps) {
  const { tone: selectedSkyTone, setTone: setSkyTone } = useSkyTheme();
  const skyTone = sceneTone ?? selectedSkyTone;
  const labels = getLabels(locale);
  const identity = useLandingIdentity();
  const authHref = buildAppLoginUrl(identity.kind === "authenticated", new URL(window.location.href));
  const signUpHref = buildAppAuthUrl("register", new URL(window.location.href));
  const [activeMenu, setActiveMenu] = useState<HeaderMenuId | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<HeaderMenuId | null>(null);
  const [menuAnchorLeft, setMenuAnchorLeft] = useState(24);
  const [themeOpen, setThemeOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  const switchTimerRef = useRef<number | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const triggerRefs = useRef<Partial<Record<HeaderMenuId, HTMLButtonElement | null>>>({});
  const drawerToggleRef = useRef<HTMLButtonElement>(null);
  const themeToggleRef = useRef<HTMLButtonElement>(null);
  const menuLabels: Record<HeaderMenuId, string> = { agents: labels.agents, tools: labels.tools, integrations: labels.integrations, resources: labels.resources };

  function cancelClose() {
    if (closeTimerRef.current !== null) { window.clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
  }
  function cancelSwitch() {
    if (switchTimerRef.current !== null) { window.clearTimeout(switchTimerRef.current); switchTimerRef.current = null; }
  }
  function scheduleClose() {
    cancelClose();
    cancelSwitch();
    closeTimerRef.current = window.setTimeout(() => { setActiveMenu(null); closeTimerRef.current = null; }, 160);
  }
  function closeAll() { cancelClose(); cancelSwitch(); setActiveMenu(null); setDrawerOpen(false); setThemeOpen(false); }
  function dismissMenu() { cancelClose(); cancelSwitch(); setActiveMenu(null); }
  const updateMenuAnchor = useCallback((menu: HeaderMenuId) => {
    const trigger = triggerRefs.current[menu];
    if (trigger) setMenuAnchorLeft(trigger.getBoundingClientRect().left);
  }, []);
  function openMenu(menu: HeaderMenuId) {
    cancelClose();
    cancelSwitch();
    setThemeOpen(false);
    updateMenuAnchor(menu);
    setActiveMenu(menu);
  }
  function previewMenu(menu: HeaderMenuId) {
    cancelClose();
    cancelSwitch();
    if (!activeMenu || activeMenu === menu) {
      openMenu(menu);
      return;
    }
    switchTimerRef.current = window.setTimeout(() => {
      updateMenuAnchor(menu);
      setActiveMenu(menu);
      switchTimerRef.current = null;
    }, 110);
  }

  useEffect(() => {
    function closeFromDocument() {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      if (switchTimerRef.current !== null) {
        window.clearTimeout(switchTimerRef.current);
        switchTimerRef.current = null;
      }
      setActiveMenu(null);
      setDrawerOpen(false);
      setThemeOpen(false);
    }
    function handlePointerDown(event: PointerEvent) {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) closeFromDocument();
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      if (themeOpen) themeToggleRef.current?.focus();
      else if (activeMenu) triggerRefs.current[activeMenu]?.focus();
      else if (drawerOpen) drawerToggleRef.current?.focus();
      closeFromDocument();
    }
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
      if (switchTimerRef.current !== null) window.clearTimeout(switchTimerRef.current);
    };
  }, [activeMenu, drawerOpen, themeOpen]);

  useEffect(() => {
    if (!activeMenu) return;
    function handleResize() { updateMenuAnchor(activeMenu as HeaderMenuId); }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeMenu, updateMenuAnchor]);

  useEffect(() => {
    if (!drawerOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [drawerOpen]);

  const activeItems = activeMenu ? getMenuItems(activeMenu, locale) : [];
  const viewAllLabel = activeMenu === "agents" ? labels.viewAgents : activeMenu === "tools" ? labels.viewTools : activeMenu === "integrations" ? labels.viewIntegrations : labels.viewResources;
  const viewAllHref = activeMenu === "tools" ? "/tools" : activeMenu ? `/${activeMenu}` : "/";
  const authPending = identity.kind === "loading";
  // Until the session bootstrap resolves, paint the last known account state so a signed-in
  // visitor never sees guest controls; without one, keep a neutral placeholder rather than
  // claiming an identity. The real state replaces both as soon as it is known.
  const accountHint = authPending ? readAccountStateHint() : undefined;
  const accountEntry = identity.kind === "authenticated"
    ? <Link className={styles.login} href={buildAppLoginUrl(true, new URL(window.location.href))} onClick={closeAll}>{labels.dashboard}</Link>
    : identity.kind === "guest"
      ? <Link className={styles.login} href={authHref} onClick={closeAll}>{labels.login}</Link>
      : accountHint
        ? <button className={styles.login} type="button" disabled aria-busy="true" data-auth-pending="true">{accountHint === "authenticated" ? labels.dashboard : labels.login}</button>
        : <span className={`${styles.login} ${styles.pending} ${styles.pendingAccount}`} data-auth-pending="true" aria-hidden="true" />;
  const signUpEntry = identity.kind === "authenticated"
    ? null
    : authPending
      ? accountHint === "authenticated"
        ? null
        : accountHint === "guest"
          ? <button className={styles.secondaryCta} type="button" disabled aria-busy="true" data-auth-pending="true">{labels.signUp}</button>
          : <span className={`${styles.secondaryCta} ${styles.pending} ${styles.pendingSignUp}`} data-auth-pending="true" aria-hidden="true" />
      : <Link className={styles.secondaryCta} href={signUpHref} onClick={closeAll}>{labels.signUp}</Link>;

  return (
    <header ref={headerRef} className={`${styles.header} ${styles[variant]} ${styles[tone]}`} data-intro-complete={introComplete} data-scrolled={scrolled} data-sky-tone={skyTone} data-theme-control={showThemeControl} style={getHeaderSkyStyle(skyTone)} lang={locale === "zh" ? "zh-CN" : "en"}>
      <nav className={styles.bar} aria-label={locale === "zh" ? "主导航" : "Primary navigation"}>
        <div className={styles.desktopNav} onMouseLeave={scheduleClose}>
          {(["agents", "tools", "integrations"] as const).map((menu) => <div className={styles.menuTrigger} key={menu} onMouseEnter={() => previewMenu(menu)}><button ref={(node) => { triggerRefs.current[menu] = node; }} type="button" aria-expanded={activeMenu === menu} aria-controls="primary-mega-menu" onClick={() => openMenu(menu)}>{menuLabels[menu]}<ChevronDown aria-hidden="true" /></button></div>)}
          <Link href="/use-cases" onMouseEnter={dismissMenu} onClick={closeAll}>{labels.useCases}</Link>
          <div className={styles.menuTrigger} onMouseEnter={() => previewMenu("resources")}><button ref={(node) => { triggerRefs.current.resources = node; }} type="button" aria-expanded={activeMenu === "resources"} aria-controls="primary-mega-menu" onClick={() => openMenu("resources")}>{labels.resources}<ChevronDown aria-hidden="true" /></button></div>
          <Link href={`/blog?lang=${locale}`} onMouseEnter={dismissMenu} onClick={closeAll}>{labels.blog}</Link>
        </div>

        <Link className={styles.wordmark} href="/" aria-label="Zesrin home" data-air-logo-target={variant === "air" ? "" : undefined} tabIndex={variant === "air" && !introComplete ? -1 : 0}><Image src="/landing/assets/zesrin-logo.svg" alt="" width={125} height={41} priority /></Link>

        <div className={styles.actions}>
          <div className={styles.themeControl}>
            <button ref={themeToggleRef} className={styles.themeToggle} type="button" aria-label={locale === "zh" ? "切换天空时段" : "Change sky time"} aria-expanded={themeOpen} aria-controls="sky-theme-options" onClick={() => { setActiveMenu(null); setThemeOpen((open) => !open); }}><SkyToneIcon tone={skyTone} /></button>
            <div id="sky-theme-options" className={styles.themeOptions} role="group" aria-label={locale === "zh" ? "天空时段" : "Sky time"} data-open={themeOpen} aria-hidden={!themeOpen} inert={!themeOpen}>
              {SKY_TONES.map((item) => <button key={item} type="button" aria-label={locale === "zh" ? ({ sunrise: "日出", sun: "日间", sunset: "日落", moon: "月夜" } as const)[item] : ({ sunrise: "Sunrise", sun: "Day", sunset: "Sunset", moon: "Moonlight" } as const)[item]} aria-pressed={skyTone === item} onClick={() => { setSkyTone(item); setThemeOpen(false); themeToggleRef.current?.focus(); }}><SkyToneIcon tone={item} /><span>{locale === "zh" ? ({ sunrise: "日出", sun: "日间", sunset: "日落", moon: "月夜" } as const)[item] : ({ sunrise: "Sunrise", sun: "Day", sunset: "Sunset", moon: "Moonlight" } as const)[item]}</span></button>)}
            </div>
          </div>
          {onLocaleChange && <button className={styles.languageButton} type="button" onClick={onLocaleChange} aria-label={labels.language}>{locale === "en" ? "中文" : "EN"}</button>}
          {variant === "air" && signUpEntry}
          {accountEntry}
          {variant === "site" && <Link className={styles.cta} href={authHref} onClick={closeAll}>{labels.cta}<ArrowRight aria-hidden="true" /></Link>}
          <button ref={drawerToggleRef} className={styles.drawerToggle} type="button" aria-label={labels.menu} aria-expanded={drawerOpen} aria-controls="mobile-navigation" onClick={() => { cancelClose(); cancelSwitch(); setActiveMenu(null); setThemeOpen(false); setDrawerOpen((open) => !open); }}>{drawerOpen ? <X aria-hidden="true" /> : <List aria-hidden="true" />}</button>
        </div>
      </nav>

      <button className={styles.backdrop} data-open={Boolean(activeMenu) || drawerOpen || themeOpen} type="button" aria-label={labels.close} aria-hidden="true" tabIndex={-1} onClick={closeAll} />

      <div id="primary-mega-menu" className={styles.megaMenu} style={{ "--header-menu-anchor-left": `${menuAnchorLeft}px` } as CSSProperties} data-open={Boolean(activeMenu)} aria-hidden={!activeMenu} inert={!activeMenu} onMouseEnter={() => { cancelClose(); cancelSwitch(); }} onMouseLeave={scheduleClose}>
        {activeMenu && <div className={styles.megaContent} key={activeMenu}>
          <div className={styles.menuLinks}>
            <div className={styles.menuHeading}><span>{menuLabels[activeMenu]}</span>{activeMenu === "integrations" ? <span aria-disabled="true">{viewAllLabel} · {locale === "zh" ? "规划中" : "Planned"}</span> : <Link href={viewAllHref} onClick={closeAll}>{viewAllLabel}<ArrowRight aria-hidden="true" /></Link>}</div>
            <div className={styles.menuGrid} data-menu={activeMenu}>{activeItems.map((item) => {
              const ItemIcon = item.icon;
              const content = <><span className={styles.itemIcon}><ItemIcon aria-hidden="true" /></span><span><strong>{item.title}{item.meta && <em>{item.meta}</em>}</strong><small>{item.description}</small></span></>;
              return item.disabled
                ? <div key={`${item.href}-${item.title}`} className={styles.disabledItem} aria-disabled="true">{content}</div>
                : <Link key={`${item.href}-${item.title}`} href={item.href} onClick={closeAll}>{content}<ArrowRight className={styles.itemArrow} aria-hidden="true" /></Link>;
            })}</div>
          </div>
        </div>}
      </div>

      <div id="mobile-navigation" className={styles.mobileDrawer} data-open={drawerOpen} aria-hidden={!drawerOpen} inert={!drawerOpen}>
        {(["agents", "tools", "integrations", "resources"] as const).map((menu) => { const open = mobileSection === menu; const items = getMenuItems(menu, locale); const viewAllHref = menu === "tools" ? "/tools" : `/${menu}`; return <div className={styles.mobileSection} key={menu}><button type="button" aria-expanded={open} aria-controls={`mobile-${menu}`} onClick={() => setMobileSection((current) => current === menu ? null : menu)}>{menuLabels[menu]}<ChevronDown data-open={open} aria-hidden="true" /></button><div id={`mobile-${menu}`} className={styles.mobileList} data-open={open} aria-hidden={!open} inert={!open}>{items.map((item) => {
          const ItemIcon = item.icon;
          const content = <><ItemIcon aria-hidden="true" /><span><strong>{item.title}{item.disabled && item.meta && <em>{item.meta}</em>}</strong><small>{item.description}</small></span></>;
          return item.disabled
            ? <div key={`${menu}-${item.title}`} className={styles.mobileDisabledItem} aria-disabled="true">{content}</div>
            : <Link key={`${menu}-${item.title}`} href={item.href} onClick={closeAll}>{content}</Link>;
        })}{menu === "integrations" ? <span className={styles.mobileViewAll} aria-disabled="true">{labels.viewIntegrations} · {locale === "zh" ? "规划中" : "Planned"}</span> : <Link className={styles.mobileViewAll} href={viewAllHref} onClick={closeAll}>{menu === "agents" ? labels.viewAgents : menu === "tools" ? labels.viewTools : labels.viewResources}<ArrowRight aria-hidden="true" /></Link>}</div></div>; })}
        <Link className={styles.mobileDirect} href="/use-cases" onClick={closeAll}>{labels.useCases}<ArrowRight aria-hidden="true" /></Link>
        <Link className={styles.mobileDirect} href={`/blog?lang=${locale}`} onClick={closeAll}>{labels.blog}<ArrowRight aria-hidden="true" /></Link>
        <div className={styles.mobileActions}>
          {onLocaleChange && <button type="button" onClick={() => { onLocaleChange(); closeAll(); }}>{locale === "en" ? "中文" : "English"}</button>}
          {variant === "air" && signUpEntry}
          {accountEntry}
          {variant === "site" && <Link className={styles.cta} href={authHref} onClick={closeAll}>{labels.cta}</Link>}
        </div>
      </div>
    </header>
  );
}
